import { Injectable, Logger } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { PrismaService } from '@cart/api-data-access';
import { MediaAssetService } from './media-asset.service';
import { StorageAdapterResolver } from './storage-adapter-resolver.service';

export interface ReconciliationReport {
  storageConfigId: string;
  organizationId: string;
  /** In the bucket, not in the registry — we are paying to store these. */
  orphanKeys: string[];
  /** In the registry, not in the bucket — these render as broken images. */
  missingKeys: string[];
  scannedObjects: number;
  registeredAssets: number;
}

/**
 * Nightly comparison of what each backend holds against what the registry
 * believes it holds.
 *
 * **Report only — it never deletes anything.** Auto-deleting "orphans" is how
 * storage systems lose customer data: an in-flight upload, a half-finished
 * migration, or a bug in this very comparison all look exactly like an orphan
 * from here. A human decides; this job's job is to make sure a human can.
 */
@Injectable()
export class MediaReconciliationService {
  private readonly logger = new Logger(MediaReconciliationService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly resolver: StorageAdapterResolver,
    private readonly assets: MediaAssetService
  ) {}

  @Cron('30 3 * * *')
  async reconcileAll(): Promise<void> {
    const configs = await this.prisma.merchantStorageConfig.findMany({
      where: { status: { in: ['active', 'retained'] }, mode: { not: 'local_disk' } },
      select: { id: true },
    });

    let orphans = 0;
    let missing = 0;
    for (const config of configs) {
      try {
        const report = await this.reconcileConfig(config.id);
        orphans += report.orphanKeys.length;
        missing += report.missingKeys.length;
        if (report.orphanKeys.length || report.missingKeys.length) {
          this.logger.warn(
            `Reconciliation ${config.id}: ${report.orphanKeys.length} orphan(s), ${report.missingKeys.length} missing`
          );
        }
      } catch (err) {
        this.logger.warn(`Reconciliation failed for config ${config.id}: ${err}`);
      }
    }

    this.logger.log(
      `Reconciliation swept ${configs.length} backend(s): ${orphans} orphan object(s), ${missing} missing object(s)`
    );
  }

  async reconcileConfig(storageConfigId: string): Promise<ReconciliationReport> {
    const config = await this.resolver.configById(storageConfigId);
    const adapter = await this.resolver.forConfig(config, 'health_check');

    const bucketKeys = new Set<string>();
    let cursor: string | undefined;
    let pages = 0;
    do {
      const page = await adapter.list(`${config.organizationId}/`, cursor);
      page.keys.forEach(k => bucketKeys.add(k));
      cursor = page.nextCursor;
      pages += 1;
      // A ceiling so one enormous merchant cannot make the nightly job run
      // forever; the remainder is picked up on the next night's pass.
    } while (cursor && pages < 100);

    const assets = await this.prisma.mediaAsset.findMany({
      where: { storageConfigId },
      select: { storageKey: true },
    });
    const registryKeys = new Set(assets.map(a => a.storageKey));

    const orphanKeys: string[] = [];
    for (const key of bucketKeys) {
      // The probe prefix is ours, written and deleted by testConnection().
      if (key.includes('/.platform-probe/')) continue;
      if (!registryKeys.has(key)) orphanKeys.push(key);
    }

    const missingKeys: string[] = [];
    for (const key of registryKeys) {
      if (!bucketKeys.has(key)) missingKeys.push(key);
    }

    return {
      storageConfigId,
      organizationId: config.organizationId,
      orphanKeys: orphanKeys.slice(0, 500),
      missingKeys: missingKeys.slice(0, 500),
      scannedObjects: bucketKeys.size,
      registeredAssets: registryKeys.size,
    };
  }

  /**
   * Retries deletes that failed earlier.
   *
   * A failed delete leaves the row as `purge_failed` rather than removing it:
   * dropping the row would leak an object we keep paying for and can no longer
   * find. After enough attempts the row stops being retried and stays visible
   * as a real, investigable problem instead of disappearing quietly.
   */
  @Cron('45 3 * * *')
  async retryFailedPurges(): Promise<void> {
    const stuck = await this.assets.listPurgeRetryable(200);
    if (!stuck.length) return;

    let purged = 0;
    for (const asset of stuck) {
      if (await this.assets.purge(asset.id)) purged += 1;
    }
    this.logger.log(`Purge retry: ${purged}/${stuck.length} succeeded`);
  }
}
